import { cleanPhoneNumber } from '@/utils/formatPhoneNumber'

/** Índice do contato no back (`db_id`). NUNCA é dado exibido — só confirmação. */
export const PARAM_CONTACT_ID = 'to'
/** Chave de busca do lookup por telefone. NUNCA é dado exibido. */
export const PARAM_PHONE = 'phone'
/** Canal de saída pré-escolhido. Mesmo nome de param do Disparos (`src/lib/dispatches.ts`)
 *  de propósito: link colado num chamado é conferível contra as duas telas sem tradução
 *  mental. NUNCA é dado exibido — só vale se casar com um canal de `useChatBot().channels`
 *  (`seedChannelId`), senão um link forjado escolheria canal por fora do escopo. */
export const PARAM_CHANNEL_ID = 'channel_id'
/** Nome do template a semear (a chave real da Meta é o par name+language, nunca o db_id:
 *  o db_id vem uuid novo a cada `from_api`). NUNCA é dado exibido. */
export const PARAM_TEMPLATE = 'template'
/** Idioma do template. Sem ele o par não fecha e a semente é ignorada. */
export const PARAM_LANG = 'lang'
/** `db_id` da lista de transmissão. Só vale com `channel_id` na URL — a lista é DO CANAL. */
export const PARAM_GROUP = 'group'
/** Chave do rascunho de disparo em `sessionStorage` ([[rascunhoDeDisparo]]). PRESENTE, o wizard
 *  IGNORA as outras sementes (inclusive `channel_id`). NUNCA é dado exibido. */
export const PARAM_RASCUNHO = 'rascunho'

/** Só o que o link precisa — aceita `IContactInfo` por estrutura, sem acoplar ao tipo inteiro. */
export interface DeepLinkContact {
  db_id: string
  phone_number: string
}

/** Sementes opcionais do wizard além do contato: template (o PAR name+language) e lista. */
export interface NewConversationSeeds {
  template?: { name: string; language: string }
  group?: string
}

const NEW_CONVERSATION_PATH = '/chatbot/new-conversation'

/**
 * Deep link do wizard de nova conversa.
 *
 * O wizard é rota do TENANT: sem canal ainda existe link — quem pergunta qual é o canal é o
 * próprio wizard. Devolvia `null` enquanto morava sob `/chatbot/[channelId]`, e era isso que
 * fazia o botão "Iniciar conversa" sumir na rota estática `/chatbot/contacts`.
 *
 * `channel_id` é o mesmo nome de param do Disparos (`src/lib/dispatches.ts`) de propósito.
 * `to` é o `db_id` e só é COMPARADO com o que o back devolver; `phone` só vira chave de busca
 * do `useContactLookup`. Nenhum dos três é renderizado nem enviado. `URLSearchParams` é
 * obrigatório: o `+` tem que ir percent-encoded, senão `searchParams.get('phone')` devolve um
 * espaço no lugar dele.
 *
 * `seeds` (menu da bolha: "Encaminhar template" / "Disparar para esta lista"): `template`
 * emite `template`+`lang` JUNTOS — a chave da Meta é o par — e `group` emite `group`.
 * `name`/`language` vêm do payload do back e passam pelo mesmo encode; a lib não valida
 * canal×lista nem template×lista — quem descarta semente incompatível é o wizard.
 */
export function newConversationPath(
  channelId?: string,
  contact?: DeepLinkContact,
  seeds?: NewConversationSeeds,
): string {
  const params = new URLSearchParams()
  if (channelId) params.set(PARAM_CHANNEL_ID, channelId)
  if (contact) {
    params.set(PARAM_CONTACT_ID, contact.db_id)
    const digits = cleanPhoneNumber(contact.phone_number ?? '')
    if (digits) params.set(PARAM_PHONE, `+${digits}`)
  }
  if (seeds?.template) {
    params.set(PARAM_TEMPLATE, seeds.template.name)
    params.set(PARAM_LANG, seeds.template.language)
  }
  if (seeds?.group) params.set(PARAM_GROUP, seeds.group)
  const query = params.toString()
  return query ? `${NEW_CONVERSATION_PATH}?${query}` : NEW_CONVERSATION_PATH
}

/** Wizard semeado por rascunho. Só a chave: canal, alvo e template moram no rascunho. */
export function newConversationDraftPath(chave: string): string {
  const params = new URLSearchParams({ [PARAM_RASCUNHO]: chave })
  return `${NEW_CONVERSATION_PATH}?${params.toString()}`
}
